import { type HttpContext } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { AttachmentsService } from '@modules/attachments/services/attachments.service';
import { Attachment } from '@modules/attachments/types/attachment';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { requestContextForTenantItems } from '@modules/common/services/base-stitch.service';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { Stitch } from '@modules/common/types/stitch';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { LinkedInfo } from '@modules/linked-info/types/linked-info';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { of, Subject, zip } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.less'],
  standalone: false,
})
export class AttachmentsComponent implements OnInit, OnChanges, OnDestroy {
  // Inputs
  @Input() stitchItem: Stitch;
  @Input() receivedDate: Date = new Date();
  @Input() withActions = true;
  /**
   * The mailbox {@link stitchItem} lives in, when that is not the one on screen — the same context the
   * form was opened with (`FullFormBaseComponent.requestContext`).
   *
   * Absent, the list falls through to the ACTIVE mailbox. That is right for an item opened from the
   * mailbox being viewed and wrong for one opened from another rail row, which the mail rail does offer
   * (`side-bar-folders-tree.component.ts`) — its files would then be looked for in the wrong tenant and
   * none would show (#971).
   */
  @Input() public requestContext: HttpContext | undefined;

  @Output() clickStitchItem = new EventEmitter<Stitch>();
  @Output() dblClickStitchItem = new EventEmitter<Stitch>();

  // Public
  public readonly attachments = signal<Attachment[]>([]);

  protected readonly loading = signal(true);

  // Private
  private readonly stitchItemChanged = new Subject<void>();
  private readonly alive = new Subject<void>();
  private readonly attachmentsService = inject(AttachmentsService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly spaceContextStore = inject(SpaceContextStore);

  /**
   * Lifecycle
   */

  ngOnInit(): void {
    this.stitchItemChanged
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.stitchItem
            ? this.attachmentsService.listAll(
                this.stitchItem,
                { inline: false },
                // The item is the tenant carrier (Pavel, 2026-09-06). No mail message-open path binds
                // `requestContext`, so this read used to fall through to the mailbox on screen and 404
                // on a member message inside a team (#1051). An explicit context still wins in
                // `requestContextForTenantItems`, so #971's folders-tree path is untouched; with none,
                // the owner is the same `stitchItem.accountId` the hydration below already uses.
                requestContextForTenantItems([this.stitchItem], { requestContext: this.requestContext }),
              )
            : of([] as Attachment[]),
        ),
        switchMap((attachments) => {
          const linkedInfo = attachments
            .filter((attachment) => attachment.fileId)
            .map(
              (attachment) =>
                new LinkedInfo({
                  id: attachment.fileId,
                  itemType: StitchType.FILE,
                }),
            );
          // The files were created under the MESSAGE's tenant, so the hydration names it too — the
          // list read above being scoped is not enough, and a file fetched from the wrong mailbox is
          // dropped by the `a.file` filter below without an error anyone can see (#1010).
          return zip(this.linkedInfoService.fill(linkedInfo, this.stitchItem?.accountId), of(attachments));
        }),
        map(([linkedInfoData, attachments]) =>
          this.attachmentsService.populateAttachments(attachments, linkedInfoData),
        ),
        takeUntil(this.alive),
      )
      .subscribe((attachments) => {
        this.attachments.set(attachments.filter((a) => a.file && !a.file.deleted));
        this.loading.set(false);
      });

    this.stitchItemChanged.next();

    this.stitchEventsService
      .getEvents(this.spaceContextStore.contextId(), StitchType.FILE, [SignalStitchEventType.MOVED_TO_TRASH])
      .pipe(takeUntil(this.alive))
      .subscribe((event) => {
        const trashedIds = new Set(event.ids);
        this.attachments.update((current) => current.filter((a) => !trashedIds.has(a.fileId)));
      });

    this.stitchEventsService
      .getEvents(this.spaceContextStore.contextId(), StitchType.FILE, SignalStitchEventType.RESTORED_FROM_TRASH)
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.stitchItemChanged.next();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('stitchItem' in changes) {
      this.stitchItemChanged.next();
    }
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }
}
